import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Save, Pencil, X } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { fmtNumber } from '../components/Inventory/format';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

function toEditForm(m) {
  return { cost_center_id: m.cost_center_id || '', notes: m.notes || '' };
}

export default function InventoryLocationMapping() {
  const qc = useQueryClient();
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ cost_center_id: '', notes: '' });

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['inv-locations'],
    queryFn:  () => api.get('/inventory/locations'),
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: ['admin-overtime-cost-centers'],
    queryFn:  () => api.get('/admin/overtime-cost-centers'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/inventory/locations/${id}`, payload),
    onSuccess: (data) => {
      toast.success(
        data.items_backfilled
          ? `Mapping saved — updated ${fmtNumber(data.items_backfilled)} existing item row(s).`
          : 'Mapping saved.'
      );
      qc.invalidateQueries({ queryKey: ['inv-locations'] });
      qc.invalidateQueries({ queryKey: ['inventory-summary'] });
      qc.invalidateQueries({ queryKey: ['inventory-trend'] });
      setEditingId(null);
    },
    onError: (err) => toast.error(err.message || 'Failed to save mapping.'),
  });

  function startEdit(m) {
    setEditingId(m.id);
    setEditForm(toEditForm(m));
  }

  function handleSave(id) {
    updateMutation.mutate({
      id,
      payload: { cost_center_id: editForm.cost_center_id || null, notes: editForm.notes.trim() || null },
    });
  }

  if (isLoading) return <LoadingSpinner />;

  const visible = unmappedOnly ? locations.filter((l) => !l.cost_center_id) : locations;
  const unmappedCount = locations.filter((l) => !l.cost_center_id).length;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Inventory Location Mapping"
        subtitle="Map each raw location name from inventory imports to a cost center. Rows here are auto-registered the first time an import sees a new location — saving a mapping updates existing inventory items immediately, not just future imports."
      />

      <div style={s.actionsRow}>
        <label style={s.checkLabel}>
          <input type="checkbox" checked={unmappedOnly} onChange={(e) => setUnmappedOnly(e.target.checked)} />
          Unmapped only ({fmtNumber(unmappedCount)})
        </label>
        <span style={s.filterCount}>{visible.length} of {locations.length}</span>
      </div>

      <div style={s.card}>
        <div style={{ overflowX: 'auto' }}>
          {/* Wide table: it scrolls inside this, so the page does not. */}
        <table style={s.table}>
          <thead>
            <tr>
              {['Location (as in file)', 'Current Items', 'Cost Center', 'Notes', ''].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((m) => {
              const isEditing = editingId === m.id;
              return (
                <Fragment key={m.id}>
                  <tr style={s.tr}>
                    <td style={s.td}><strong>{m.raw_location_name}</strong></td>
                    <td style={s.td}>{fmtNumber(m.item_count)}</td>
                    <td style={s.td}>
                      {m.cost_center_id
                        ? `${m.cost_center_code} — ${m.cost_center_name}`
                        : <span style={s.unmapped}>Unmapped</span>}
                    </td>
                    <td style={{ ...s.td, color: '#9CA3AF' }}>{m.notes || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={s.iconBtn} title="Edit mapping" onClick={() => (isEditing ? setEditingId(null) : startEdit(m))}>
                        {isEditing ? <X size={13} /> : <Pencil size={13} />}
                      </button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr style={s.editRow}>
                      <td colSpan={5} style={s.editTd}>
                        <div style={s.editGrid}>
                          <div style={s.field}>
                            <label style={s.label}>Cost Center</label>
                            <select style={s.input} value={editForm.cost_center_id} onChange={(e) => setEditForm({ ...editForm, cost_center_id: e.target.value })}>
                              <option value="">Unmapped</option>
                              {costCenters.map((cc) => (
                                <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                            <label style={s.label}>Notes</label>
                            <input style={s.input} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Optional" />
                          </div>
                        </div>
                        <div style={s.editActions}>
                          <button style={s.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
                          <button style={s.saveBtn} onClick={() => handleSave(m.id)} disabled={updateMutation.isPending}>
                            <Save size={13} />
                            {updateMutation.isPending ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!visible.length && (
              <tr><td colSpan={5} style={s.empty}>
                {locations.length ? 'Every location is mapped.' : 'No locations seen yet — run an import first.'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:        { maxWidth: '1000px', margin: '0 auto' },
  actionsRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' },
  filterCount: { fontSize: '11px', color: '#9CA3AF' },
  card:        { ...card.base, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th, ...tbl.th },
  tr:          { ...tbl.tr, ...tbl.tr },
  td:          { ...tbl.td, ...tbl.td },
  unmapped:    { fontSize: '11px', fontWeight: 600, color: '#A16207', background: '#FEF3C7', padding: '2px 8px', borderRadius: '10px' },
  iconBtn:     { ...btn.icon },
  editRow:     { borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' },
  editTd:      { padding: '16px' },
  editGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' },
  field:       {},
  label:       { display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', background: '#fff' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', fontSize: '12px', fontWeight: 600,
    border: 'none', borderRadius: '6px', background: '#1B2A4A',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    padding: '8px 16px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
  },
  empty:       { textAlign: 'center', padding: '32px', color: '#9CA3AF' },
};
